import { useId } from 'react';
import { PRODUCT_NAME } from '@/lib/identity';

/**
 * The Railit lockup: the rail mark, then the wordmark.
 *
 * The mark is pure geometry in `currentColor`, so it is identical — and
 * correctly coloured — in a favicon, a collapsed sidebar, a navigation bar, an
 * email client and a print PDF, with or without a webfont and whatever the
 * surface behind it.
 */

interface LogoProps {
  /** Height in px. The width follows the aspect ratio. */
  size?: number;
  /** Mark only — for collapsed sidebars, favicons and avatars. */
  markOnly?: boolean;
  /**
   * Run the sleepers down the track, forever.
   *
   * Off by default. An animation that plays everywhere is a distraction
   * everywhere; this is for the one place a logo is allowed to move — the
   * header — and nowhere else.
   */
  animate?: boolean;
  className?: string;
}

/* The designer's outlines, from brand/railit-mark.svg (viewBox 538.04 × 544.84),
   unmodified. Split into the parts the animation needs. */
const RIM =
  'M39.83,409.2c-18.16-22.05-29.28-56.17-34.7-84.45C-40.12,88.36,223.2-91.57,421.59,50.13c2.72,3.8-3.23,5.1-5.91,6.23-23.13,9.78-49.21,15.94-73.11,26.46C202.67,144.38,85.5,261.97,39.83,409.2Z';
const TAIL =
  'M486.36,110.51c55.3,80.88,67.82,175.08,29.8,266.58-5.35,12.87-45.51,86.51-58.3,83.4-60.57-84.7-97.09-186.39-39.09-283.38,16.16-27.03,38.95-52.58,67.59-66.6Z';
/**
 * The track exactly as it is on disk — body and sleepers in ONE path, the
 * sleepers cut out by fill-rule. This is what a still logo draws, so a logo
 * that is not animating is byte-identical to brand/railit-mark.svg.
 */
const TRACK =
  'M388.36,134.64c2.21-.63,17.79-18.49,22.88-22.38,6.29-4.81,45.13-29.23,49.09-28.45,2.99.59,5.75,4.94,7.94,7.2,1.22,5.42-32.71,24.59-39.22,30-23.71,19.71-51.02,56.26-63.37,84.48-45.63,104.3,1.78,202.08,48.09,295.43-92.68,60.57-205.59,57.99-296.41-4.78-8.12-5.61-34.18-23.47-33.82-32.89.6-15.47,12.28-50.45,17.53-66.76,45.06-140,138.23-243.58,271.66-304.63,18.25-8.35,48.39-22.03,67.11-26.46,3.75-.89,6.18.65,4.3,4.32-5.86,11.42-97.79,44.03-108.6,64.91h52.8ZM362.71,158.77h-64.87l-43.75,42.24h87.5l21.12-42.24ZM326.5,228.17h-95.04l-37.71,51.26c24.86-2.2,49.36,3.11,74.08,3.18,15.37.04,30.43-6.84,46.73-1.42l11.95-53.02ZM154.52,373h168.97l-9.36-60.02-138,.17-21.6,59.85ZM359.69,475.59c-10.75-21-14.37-46.43-26.06-65.97l-192.45-.59-13.82,66.55h232.33Z';
/** The same body with the cut-outs removed, for the masked (animated) version. */
const BED =
  'M388.36,134.64c2.21-.63,17.79-18.49,22.88-22.38,6.29-4.81,45.13-29.23,49.09-28.45,2.99.59,5.75,4.94,7.94,7.2,1.22,5.42-32.71,24.59-39.22,30-23.71,19.71-51.02,56.26-63.37,84.48-45.63,104.3,1.78,202.08,48.09,295.43-92.68,60.57-205.59,57.99-296.41-4.78-8.12-5.61-34.18-23.47-33.82-32.89.6-15.47,12.28-50.45,17.53-66.76,45.06-140,138.23-243.58,271.66-304.63,18.25-8.35,48.39-22.03,67.11-26.46,3.75-.89,6.18.65,4.3,4.32-5.86,11.42-97.79,44.03-108.6,64.91h52.8Z';
/**
 * The four sleepers, top of the track to the bottom.
 *
 * In the source these are cut OUT of the track body by fill-rule — holes, not
 * shapes. That is why the animation needs a mask: a hole cannot be given a
 * colour without knowing what is behind it, and the whole point of this mark is
 * that it never needs to know. In the mask they are black on a white body, so
 * fading one out CLOSES its gap and fading it in re-opens it.
 */
const SLEEPERS = [
  'M362.71,158.77h-64.87l-43.75,42.24h87.5l21.12-42.24Z',
  'M326.5,228.17h-95.04l-37.71,51.26c24.86-2.2,49.36,3.11,74.08,3.18,15.37.04,30.43-6.84,46.73-1.42l11.95-53.02Z',
  'M154.52,373h168.97l-9.36-60.02-138,.17-21.6,59.85Z',
  'M359.69,475.59c-10.75-21-14.37-46.43-26.06-65.97l-192.45-.59-13.82,66.55h232.33Z',
];

/**
 * The mark, drawn inside a 40×40 box.
 *
 * No tile behind it. A rounded rectangle in a fixed colour has to be legible
 * against every surface it lands on, which is why the previous mark needed a
 * light tile AND a dark glyph and a note explaining which was which. The
 * artwork alone needs neither.
 */
function Mark({ animate, uid }: { animate: boolean; uid: string }) {
  const maskId = `rail-${uid}`;
  return (
    <g transform="translate(0.250 0) scale(0.073416)" fill="currentColor">
      <path d={RIM} />
      {animate ? (
        <>
          {/*
            The mask region is STATED, and it is not optional.

            Without x/y/width/height the mask defaults to a region derived from
            the object's bounding box, and Chrome then does not re-rasterise it
            when the opacity of its contents changes — so the animation ran,
            cost frames, and rendered a still image. Measured three ways:
            identical markup with an explicit region animates, without one it
            does not, and moving the CSS in or out of the SVG changes nothing.

            The numbers are the artwork's own user space (538.04 × 544.84) with
            a margin, so a sleeper near an edge is not clipped.
          */}
          <mask id={maskId} maskUnits="userSpaceOnUse" x="-20" y="-20" width="580" height="590">
            <path d={BED} fill="#fff" />
            {SLEEPERS.map((d, i) => (
              <path key={i} d={d} fill="#000" className={`rail-sleeper rail-sleeper-${i}`} />
            ))}
          </mask>
          <path d={BED} mask={`url(#${maskId})`} />
        </>
      ) : (
        <path d={TRACK} />
      )}
      <path d={TAIL} />
    </g>
  );
}

export function Logo({ size = 28, markOnly = false, animate = false, className }: LogoProps) {
  /* One mask id per instance. Two logos on a page sharing an id makes the
     second one reference the first one's mask, and the second one vanishes. */
  const uid = useId().replace(/:/g, '');

  const mark = <Mark animate={animate} uid={uid} />;

  const style = animate ? (
    /*
      The wave runs top to bottom: each sleeper closes and re-opens a beat after
      the one above it, which reads as the track moving under you rather than as
      four things blinking.

      `prefers-reduced-motion` stops it completely — not slows it. A logo that
      moves forever is exactly what that setting exists to switch off.
    */
    <style>{`
      @keyframes rail-run {
        0%, 45% { opacity: 1 }
        60%     { opacity: 0.15 }
        75%,100%{ opacity: 1 }
      }
      .rail-sleeper { animation: rail-run 1.6s ease-in-out infinite; }
      .rail-sleeper-0 { animation-delay: 0s }
      .rail-sleeper-1 { animation-delay: .12s }
      .rail-sleeper-2 { animation-delay: .24s }
      .rail-sleeper-3 { animation-delay: .36s }
      @media (prefers-reduced-motion: reduce) {
        .rail-sleeper { animation: none }
      }
    `}</style>
  ) : null;

  if (markOnly) {
    return (
      <svg
        viewBox="0 0 40 40"
        width={size}
        height={size}
        className={className}
        role="img"
        aria-label={PRODUCT_NAME}
      >
        {style}
        {mark}
      </svg>
    );
  }

  /*
    Mark first, then the name.

    ONE HONEST CAVEAT: the wordmark is set as <text> because there are no
    outlines for it, so THAT half depends on the font stack and shifts slightly
    on a cold load. The mark — which is what the favicon, the collapsed sidebar
    and every avatar actually use — is pure geometry and is unaffected. Replace
    the text with outlines when a designer supplies them, and delete this note.
  */
  return (
    <svg
      viewBox="0 0 190 48"
      width={(size * 190) / 48}
      height={size}
      className={className}
      role="img"
      aria-label={PRODUCT_NAME}
    >
      {style}
      <g transform="translate(4 4)">{mark}</g>
      <text
        x="52"
        y="33"
        fill="currentColor"
        fontSize="30"
        fontWeight={600}
        letterSpacing="-0.02em"
        fontFamily="'Geist Variable', ui-sans-serif, system-ui, sans-serif"
      >
        {PRODUCT_NAME}
      </text>
    </svg>
  );
}
